<template>
    <div ref="shoppingGuide" :style="cssProps">
        <template v-if="guide">
            <shopping-guide-header
                :title="guide.header.title"
                :subtitle="guide.header.subtitle"
                @selectedQuestionChanged="onSelectQuestion"
                @restartButtonPressed="onResetButtonPressed"
                :selected-question="selectedQuestion"
                :questions="questions"
                :box-shadow="true"
                :is-loading="isLoading"
            />
            <div class="d-flex flex-column align-center" v-if="selectedQuestion">
                <h3 class="mb-5 text-center col-12 col-sm-6">{{ selectedQuestion.question }}</h3>
                <div v-if="selectedQuestion.answer.items || selectedAnswers" class="col-12">
                    <v-row class="justify-center overflow-y-auto" :style="{ 'max-height': maxHeight }">
                        <template v-if="isNonIterableType">
                            <v-col
                                v-if="selectedQuestion.answer.type === answerType.DROP_DOWN"
                                :cols="$vuetify.breakpoint.xsOnly ? '12' : '6'"
                            >
                                <shopping-guide-dropdown
                                    :answer-items="selectedQuestion.answer.items"
                                    :selected-answers="selectedAnswers"
                                    :multi-select="selectedQuestion.answer.multiSelect"
                                    :key="selectedQuestion.id"
                                    @answersChanged="updateSelectedFilters"
                                    @answersRemoved="removeAnswers"
                                />
                            </v-col>
                            <v-col
                                v-if="selectedQuestion.answer.type === answerType.RANGE_SLIDER"
                                :cols="$vuetify.breakpoint.xsOnly ? '12' : '6'"
                            >
                                <range-slider
                                    :max="selectedQuestion.answer.range.max"
                                    :min="selectedQuestion.answer.range.min"
                                    :unit-symbol="selectedQuestion.answer.unitSymbol"
                                    :current-range-values="currentRangeValues"
                                    :key="selectedQuestion.id"
                                    @changeValue="updateSelectedFilters"
                                />
                            </v-col>
                        </template>
                        <template v-else>
                            <v-col :key="answerItem.value" v-for="answerItem in selectedQuestion.answer.items" cols="auto">
                                <component
                                    :is="getAnswerComponentBasedOnName(selectedQuestion.answer.type)"
                                    :answer-item="answerItem"
                                    :type="selectedQuestion.answer.type"
                                    @onSelected="handleAnswerClick(answerItem)"
                                    @onDeselected="unselectAnswer(answerItem)"
                                />
                            </v-col>
                        </template>
                    </v-row>
                </div>
                <div v-else>
                    <span class="text-body-1">{{ $t('defaultNoFilterLabel') }}</span>
                </div>
            </div>
            <shopping-guide-footer
                @onPressBackButton="previousQuestion"
                @onPressForwardButton="onForwardButtonPressed"
                @onPressShowAllButton="onShowAllResultsButtonPressed"
                :show-show-all-button="true"
                :amount-of-results="guide.totalItems"
            />
        </template>
        <v-snackbar v-model="hasErrors" color="error" content-class="text-center">
            {{ errorMessage }}
        </v-snackbar>
    </div>
</template>

<script lang="ts">
import { PropType, VueConstructor } from 'vue';
import { Params } from '../InstantFrontend/api/api';
import { Vue } from 'vue-property-decorator';
import ShoppingGuideDropdown from './answers/ShoppingGuideDropdown.vue';
import ShoppingGuideHeader from './components/ShoppingGuideHeader.vue';
import ShoppingGuideFooter from './components/ShoppingGuideFooter.vue';
import ShoppingGuideImage from './answers/ShoppingGuideImage.vue';
import ShoppingGuideColor from './answers/ShoppingGuideColor.vue';
import RangeSlider from './answers/ShoppingGuideRangeSlider.vue';
import { answerType, RangeValues, ShoppingGuide, ShoppingGuideAnswerItem, ShoppingGuideQuestion } from './definitions';
import { CloginJsConfig } from '../../declarations/config';
import { stringify } from 'qs';
import { DirectIntegrationParams } from '../InstantFrontend/Glue';

function isRange(answers: string[] | RangeValues): answers is RangeValues {
    return (answers as RangeValues).max !== undefined;
}

export default Vue.extend({
    name: 'ShoppingGuide',
    components: {
        RangeSlider,
        ShoppingGuideDropdown,
        ShoppingGuideHeader,
        ShoppingGuideFooter,
    },
    props: {
        config: {
            type: Object as PropType<CloginJsConfig>,
            required: true,
        },
        params: {
            type: Object as PropType<DirectIntegrationParams>,
            required: true,
        },
        injectedConfig: {
            type: Object,
            default: null,
        },
        requestedGuide: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            answerType,
            guide: null as ShoppingGuide,
            selectedQuestion: null as ShoppingGuideQuestion,
            currentFlow: null,
            errorMessage: null,
            isLoading: false,
            skipToNextQuestionAfterLoad: false,
        };
    },
    computed: {
        selectedFilters: {
            get(): Params['attrib'] {
                return this.params.attrib;
            },
            set(attrib: Params['attrib']) {
                this.$emit('input', { ...this.params, ...{ attrib } });
            },
        },
        selectedAnswers(): string[] {
            return this.selectedFilters ? (Object.values(this.selectedFilters[this.selectedQuestion.attributeName] || {}) as string[]) : [];
        },
        questions(): ShoppingGuideQuestion[] {
            return this.guide?.questions || [];
        },
        selectedQuestions(): string[] {
            return this.questions
                .filter((question: ShoppingGuideQuestion) => Object.keys(this.selectedFilters || {}).includes(question.attributeName))
                .map((question: ShoppingGuideQuestion) => question.attributeName);
        },
        // TODO: add proper type for injected config
        parsedInjectedConfig(): any | null {
            try {
                return JSON.parse(this.injectedConfig);
            } catch {
                return null;
            }
        },
        filterUrlString(): string {
            const params: Record<string, any> = {};

            if (this.currentFlow) {
                params.flow = this.currentFlow;
            }

            if (this.injectedConfig) {
                params.injectedConfig = this.injectedConfig;
            }

            params.attrib = { ...this.selectedFilters };

            if (this.requestedGuide) {
                params.guide = this.requestedGuide;
                params.attrib.cat_url = undefined;
            }

            if (this.parsedInjectedConfig?.trigger?.type === 'cat_url' && this.parsedInjectedConfig?.trigger?.value) {
                params.attrib.cat_url = [this.parsedInjectedConfig.trigger.value];
            }

            return stringify(params, { arrayFormat: 'brackets' });
        },
        currentFlowIndex(): number {
            if (this.questions) {
                return this.questions.findIndex((question: ShoppingGuideQuestion) => question.id === this.selectedQuestion.id);
            }

            return 0;
        },
        cssProps(): Record<string, string> {
            return {
                '--primary-color-bg': this.$vuetify.theme.themes.light.primary as string,
                '--primary-color-text': this.$vuetify.theme.themes.light['fl-primary-text'] as string,
                '--primary-highlight-bg': this.$vuetify.theme.themes.light['fl-sg-highlight-bg'] as string,
                '--primary-highlight-text': this.$vuetify.theme.themes.light['fl-sg-highlight'] as string,
            };
        },
        isNonIterableType(): boolean {
            return (
                this.selectedQuestion.answer.type === answerType.DROP_DOWN || this.selectedQuestion.answer.type === answerType.RANGE_SLIDER
            );
        },
        maxHeight(): string {
            const screenHeight = this.$vuetify.breakpoint.height;
            const headerHeight = 170;
            const footerHeight = 70;
            const headlineHeight = 40;
            const additionalSpacings = 40;

            const maxHeight = Math.min(500, screenHeight - headerHeight - footerHeight - headlineHeight - additionalSpacings);

            return `${maxHeight}px`;
        },
        hasErrors(): boolean {
            return this.errorMessage !== null;
        },
        currentRangeValues(): RangeValues | null {
            if (this.selectedFilters && this.selectedFilters[this.selectedQuestion.attributeName]) {
                return this.selectedFilters[this.selectedQuestion.attributeName] as RangeValues;
            }

            return null;
        },
    },
    watch: {
        guide() {
            if (this.guide) {
                this.selectedQuestion = this.selectedQuestion ? this.questions[this.currentFlowIndex] : this.questions[0];
            }
        },
        filterUrlString() {
            void this.loadData();
        },
        isLoading() {
            if (!this.isLoading && this.skipToNextQuestionAfterLoad) {
                this.nextQuestion();
                this.skipToNextQuestionAfterLoad = false;
            }
        },
    },
    methods: {
        updateSelectedFilters(answers: string[] | RangeValues) {
            this.selectedFilters = { ...this.selectedFilters, [this.selectedQuestion.attributeName]: answers };

            if (!isRange(answers) && !this.selectedQuestion.answer.multiSelect && answers.length) {
                this.skipToNextQuestionAfterLoad = true;
            }
        },
        removeAnswers(attributeList: string[] | null = null) {
            const list = attributeList || [this.selectedQuestion.attributeName];
            const selectedFilters = this.selectedFilters;

            list.forEach((attributeName: string) => {
                delete this.selectedFilters[attributeName];
            });

            this.selectedFilters = selectedFilters;
        },
        handleAnswerClick(answer: ShoppingGuideAnswerItem) {
            if (answer.flow) {
                this.skipToNextQuestionAfterLoad = true;

                this.removeAnswers(this.selectedQuestions);
                this.currentFlow = answer.flow;
            } else {
                const answers = [answer.value];

                if (this.selectedQuestion.answer.multiSelect && this.selectedAnswers.length) {
                    answers.unshift(...this.selectedAnswers);
                }

                this.updateSelectedFilters(answers);
            }
        },
        unselectAnswer(answer: ShoppingGuideAnswerItem) {
            if (answer.flow) {
                this.currentFlow = null;
                this.removeAnswers(this.selectedQuestions);
            } else {
                const selectedValues = this.selectedAnswers.filter((value: string) => value !== answer.value);

                if (!selectedValues.length) {
                    this.removeAnswers();
                } else {
                    this.updateSelectedFilters(selectedValues);
                }
            }
        },
        async loadData() {
            try {
                this.isLoading = true;
                const response = await fetch(
                    `https://service.findologic.com/${this.config.shopkey}/shopping-guide?${this.filterUrlString}`
                );
                const parsedResponse = await response.json();
                if (parsedResponse.data.result) {
                    this.guide = parsedResponse.data.result as ShoppingGuide;
                }
            } catch {
                this.setErrorMessage(this.$t('shoppingGuide.error.canNotLoad') as string);
            } finally {
                this.isLoading = false;
            }
        },
        nextQuestion() {
            if (this.currentFlowIndex < this.questions.length - 1) {
                this.selectedQuestion = this.questions[this.currentFlowIndex + 1];
            }
        },
        previousQuestion() {
            if (this.currentFlowIndex > 0) {
                this.selectedQuestion = this.questions[this.currentFlowIndex - 1];
            }
        },
        onSelectQuestion(question: ShoppingGuideQuestion) {
            if (this.isNextQuestionBlocked(question)) {
                return;
            }

            this.selectedQuestion = question;
        },
        onForwardButtonPressed() {
            if (this.isNextQuestionBlocked()) {
                return;
            }

            this.nextQuestion();
        },
        onResetButtonPressed() {
            this.selectedQuestion = this.questions[0];
            this.$emit('input', {});
        },
        getAnswerComponentBasedOnName(type: answerType): VueConstructor | null {
            switch (type) {
                case answerType.IMAGE_WITH_TEXT_SMALL:
                case answerType.IMAGE_WITH_TEXT_MEDIUM:
                case answerType.IMAGE_WITH_TEXT_BIG:
                    return ShoppingGuideImage;
                case answerType.COLOR_SELECTOR:
                    return ShoppingGuideColor;
                case answerType.DROP_DOWN:
                    return null;
                default:
                    throw new Error(`${type} is not an answer component`);
            }
        },
        onShowAllResultsButtonPressed() {
            const rect = (this.$refs.shoppingGuide as HTMLElement).getBoundingClientRect();
            window.scrollBy({
                top: rect.height + rect.top,
                behavior: 'smooth',
            });
        },
        isNextQuestionBlocked(newQuestion?: ShoppingGuideQuestion) {
            const noRequiredAnswer = this.selectedQuestion.required && !this.selectedAnswers?.length;
            const isNextQuestion = newQuestion ? this.questions.indexOf(newQuestion) > this.questions.indexOf(this.selectedQuestion) : true;

            if (noRequiredAnswer && isNextQuestion) {
                this.setErrorMessage(this.$t('shoppingGuide.validation.required') as string);

                return true;
            } else {
                this.resetError();

                return false;
            }
        },
        setErrorMessage(errorMessage: string) {
            this.errorMessage = errorMessage;
        },
        resetError() {
            this.errorMessage = null;
        },
    },
    mounted() {
        void this.loadData();
    },
});
</script>

<style lang="scss">
.v-application {
    .primary {
        color: var(--primary-color-text) !important;
        fill: var(--primary-color-text) !important;
    }

    .v-application--wrap {
        min-height: 0;
    }
}
</style>
